import { useState } from 'react';

import { faKeyboard } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { faArrowDownRight } from '@awesome.me/kit-f3dc60f0d0/icons/duotone/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLingui } from '@lingui/react/macro';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import { PopoverPortal } from '@repo/videa-components/src/ui/popover';

import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ToolbarButton } from './ToolbarButton';

interface ShortcutsProps {
  triggerIcon?: React.ReactNode;
  patientViewEnabled?: boolean;
  toothPartsEnabled?: boolean;
}

export const Shortcuts: React.FC<ShortcutsProps> = ({
  triggerIcon,
  patientViewEnabled = false,
  toothPartsEnabled = false,
}) => {
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();
  const [isOpen, setIsOpen] = useState(false);

  const shortcuts = [
    { key: 'A', description: t`Show/hide all Annotations` },
    ...(patientViewEnabled
      ? [{ key: 'V', description: t`Turn on/off Patient View` }]
      : []),
    ...(toothPartsEnabled
      ? [{ key: 'T', description: t`Turn on/off Tooth Parts Segmentation` }]
      : []),
    { key: 'R', description: t`Show/hide Restorative Annotations` },
    { key: 'P', description: t`Show/hide Perio Annotations` },
    { key: 'E', description: t`Show/hide Endo Annotations` },
    { key: '←', description: t`Previous Radiograph` },
    { key: '→', description: t`Next Radiograph` },
    { key: '.', description: t`Rotate` },
    { key: 'H', description: t`HD` },
    { key: 'M', description: t`Magnify` },
    { key: 'I', description: t`Invert` },
    { key: '1', description: t`Window Level (brightness) 1` },
    { key: '2', description: t`Window Level (brightness) 2` },
    { key: '3', description: t`Window Level (brightness) 3` },
    { key: '4', description: t`Window Level (brightness) 4` },
    { key: '5', description: t`Window Level (brightness) 5` },
    { key: '6', description: t`Window Level (brightness) 6` },
    { key: 'D', description: t`Reset Toolbar Settings` },
  ];

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <div className="vai-relative">
              <ToolbarButton
                isActive={isOpen}
                onClick={() => trackEvent(mixpanelEvents.openShortcuts)}
              >
                {triggerIcon ?? (
                  <FontAwesomeIcon
                    icon={faKeyboard}
                    className="vai-text-slate-200 vai-text-lg"
                  />
                )}
                <FontAwesomeIcon
                  icon={faArrowDownRight}
                  className="vai-text-slate-300 vai-absolute max-[880px]:vai-right-[-2px] max-[880px]:vai-bottom-[-2px] min-[880px]:vai-right-[-3px] min-[880px]:vai-bottom-[-3px] max-[880px]:!vai-size-1.5 min-[880px]:!vai-size-2.5 [--fa-secondary-opacity:0]"
                />
              </ToolbarButton>
            </div>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent
          variant="dark"
          side="top"
          sideOffset={8}
          alignOffset={-4}
        >
          <p>{t`Keyboard Shortcuts`}</p>
        </TooltipContent>
      </Tooltip>
      {/* Portal ensures that that popover appears above all other elements */}
      <PopoverPortal>
        <PopoverContent
          side="right"
          align="start"
          sideOffset={8}
          alignOffset={-4}
          className="vai-w-[320px] vai-p-5 vai-bg-slate-800 vai-border vai-border-slate-700 vai-shadow-xl"
        >
          <h3 className="vai-text-xs vai-font-semibold vai-text-gray-200 vai-mb-3">
            Keyboard Shortcuts
          </h3>

          <div className="vai-space-y-2">
            {shortcuts.map((shortcut, index) => (
              <div key={index} className="vai-flex vai-items-center">
                <span className="vai-min-w-[2rem] vai-px-2 vai-py-1 vai-bg-slate-700 vai-rounded vai-text-xs vai-font-mono vai-font-medium vai-text-gray-200 vai-text-center vai-mr-3">
                  {shortcut.key}
                </span>
                <span className="vai-text-xs vai-text-gray-300 vai-flex-1">
                  {shortcut.description}
                </span>
              </div>
            ))}
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};
